import { useCallback } from 'react';
import {
  usePaginatedDropdown,
  PaginatedRequest,
  PaginatedResponse,
} from '@/common/helper/usePaginatedDropdown';

export interface UseLiteDropdownOptions<T> {
  fetchFn: (params: PaginatedRequest) => Promise<PaginatedResponse<T>>;
  initialLimit?: number;
  initialSearch?: string;
  filterType?: string;
}

/**
 * Middle-layer hook between usePaginatedDropdown and domain hooks.
 *
 * Responsibilities:
 *  - Delegates all data/pagination state to usePaginatedDropdown
 *  - Owns the UI scroll bridge (handlePopupScroll) so domain hooks
 *    don't duplicate it
 *
 * Domain hooks (useRoleLiteDropdown, useTeamLiteDropdown, …) only need
 * to supply a fetchFn — they get everything else for free.
 */
export function useLiteDropdown<T>({
  fetchFn,
  initialLimit = 20,
  initialSearch = '',
  filterType,
}: UseLiteDropdownOptions<T>) {
  const paginated = usePaginatedDropdown<T>({
    fetchFn,
    initialLimit,
    initialSearch,
    filterType,
  });

  /**
   * Attach to onPopupScroll on any dropdown.
   * Fires loadMore() when the user scrolls within 24 px of the bottom,
   * but only when there are more pages available.
   */
  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!paginated.hasMore || paginated.loading) return;
      const target = e.currentTarget;
      if (!target) return;
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (nearBottom) {
        paginated.loadMore();
      }
    },
    [paginated]
  );

  return {
    data: paginated.data,
    hasMore: paginated.hasMore,
    loading: paginated.loading,
    search: paginated.search,
    setSearch: paginated.setSearch,
    loadMore: paginated.loadMore,
    total: paginated.total,
    /** Attach to onPopupScroll to auto-trigger infinite loading */
    handlePopupScroll,
    fetchAllMatching: paginated.fetchAllMatching,
  };
}

export default useLiteDropdown;
